/**
 * Swamithra — the numbers the product is specified in.
 *
 * Every one of these appears in an acceptance criterion, and every one is
 * enforced in at least two places (a client-side guard and a server-side rule).
 * They live here so the three clients — web, panel, Expo — cannot disagree about
 * what "the 30-minute lockout" or "the 5 MB limit" is.
 *
 * These are the CLIENT's copy of a server-owned rule. The server remains the
 * authority: a client-side check exists to give a fast, specific message, never
 * to be the only thing standing between a user and a violated rule.
 */

// -----------------------------------------------------------------------------
// Passwordless authentication (US-00-001, US-01-005)
// -----------------------------------------------------------------------------

/** Digits in the WhatsApp OTP. */
export const OTP_LENGTH = 6

/** How long a dispatched code stays valid. */
export const OTP_VALIDITY_MINUTES = 10

/**
 * Wrong codes tolerated before the NUMBER (not the device) is locked.
 *
 * Counted server-side only: a client that never reached the server has not used
 * an attempt, which is what makes US-00-001.21 work.
 */
export const OTP_MAX_VERIFICATION_ATTEMPTS = 3

/** Lockout duration once the attempt ceiling is hit. */
export const OTP_LOCKOUT_MINUTES = 30

/**
 * Minimum gap between resends, used to disable the 'Resend code' control.
 *
 * The server sends `resendAvailableAt` on every OTP request and that value wins;
 * this is the fallback when a response did not carry one.
 */
export const OTP_RESEND_COOLDOWN_SECONDS = 30

/** Ceiling on resends per number per rolling hour. Enforced server-side. */
export const OTP_RESENDS_PER_HOUR = 5

/**
 * The disclaimer a person ticks before a sign-in code can be requested, on the
 * user app (web and Expo) and the Expert panel alike. One string so the three
 * sign-in screens cannot drift apart. Client wording, verbatim.
 */
export const SIGN_IN_DISCLAIMER =
	"Responses received in this platform are voluntary and for knowledge sharing purposes and informed choices may be made at your own discretion."

/**
 * Idle window before a session is rejected.
 *
 * "Idle" means no authenticated request: every authenticated request slides the
 * expiry forward (US-00-001.14, US-01-005.12).
 */
export const SESSION_IDLE_DAYS = 5

export const SESSION_IDLE_MS = SESSION_IDLE_DAYS * 24 * 60 * 60 * 1000

// -----------------------------------------------------------------------------
// Attachments (US-00-002, US-00-004, US-00-006)
// -----------------------------------------------------------------------------

/**
 * Per-file ceiling, in bytes.
 *
 * 5 MiB — 5 × 1024 × 1024 — because that is the number the SERVER enforces, and
 * the two have to be the same number. Read as decimal megabytes the client
 * refused every file between 5,000,001 and 5,242,880 bytes with a message
 * quoting a limit the server would have accepted: a user shown "5 MB or smaller"
 * for a file the API calls 5.00 MB has been told something untrue.
 */
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024

/** Attachments allowed on one query or one follow-up message. */
export const MAX_ATTACHMENTS_PER_SUBMISSION = 3

/**
 * Lifetime of a download URL minted by the API.
 *
 * The client never caches a signed URL beyond this: it asks for a fresh one per
 * open, so a replayed expired URL is a case the user never reaches
 * (US-00-006.6).
 */
export const SIGNED_URL_TTL_SECONDS = 900

// -----------------------------------------------------------------------------
// Query lifecycle (US-01-001, US-01-003)
// -----------------------------------------------------------------------------

/** First-response target. Past this, a query row renders as a breach. */
export const SLA_FIRST_RESPONSE_HOURS = 24

/** Where the row moves from neutral to warning treatment. */
export const SLA_WARNING_HOURS = 20

/**
 * Inactivity before an answered/in-progress query auto-closes.
 *
 * An ABSOLUTE duration, deliberately: 7 calendar days evaluated in IST would
 * shift eligibility by up to five and a half hours depending on when the reply
 * landed (US-01-003.19).
 */
export const INACTIVITY_AUTO_CLOSE_HOURS = 168

/** Trailing window for the average-response-time figure (US-00-002.4). */
export const AVERAGE_RESPONSE_WINDOW_DAYS = 7

/** Characters allowed in the optional feedback comment (US-00-002.15). */
export const FEEDBACK_COMMENT_MAX_LENGTH = 500

// -----------------------------------------------------------------------------
// Pagination
// -----------------------------------------------------------------------------

/** Fixed by US-01-001.10. Not a user preference. */
export const EXPERT_DASHBOARD_PAGE_SIZE = 25

/** Numbered pages on web (US-00-002.8, US-00-004.8). */
export const WEB_LIST_PAGE_SIZE = 20

/** One page of an infinite-scroll list on mobile. */
export const MOBILE_LIST_PAGE_SIZE = 20

// -----------------------------------------------------------------------------
// Free-text ceilings
//
// The server rejects past these with 422; the client blocks input at the same
// number so the rejection is not how the user finds out.
// -----------------------------------------------------------------------------

export const QUERY_TITLE_MAX_LENGTH = 200
/** Visible characters (richTextVisibleLength), not markup. */
export const QUERY_DESCRIPTION_MAX_LENGTH = 3000
/** Visible characters (richTextVisibleLength), not markup. */
export const VETTING_NOTES_MAX_LENGTH = 3000
export const RICH_TEXT_MAX_LENGTH = 20_000
